import { isJSONString, isCampaignPath, isFsmUnderCampaignPath } from './formatting';

// Helper function to parse JSON strings if they are valid JSON
export const parseIfJSON = (value: unknown): unknown => {
  if (typeof value === 'string' && isJSONString(value)) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

// Helper function to create a key for array item matching
export const getItemKey = (item: unknown): string => {
  if (item == null) return String(item);
  if (typeof item === 'object' && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    // Try to find a unique identifier (id, _id, key, etc.)
    if ('id' in obj && obj.id != null) return `id:${obj.id}`;
    if ('_id' in obj && obj._id != null) return `_id:${obj._id}`;
    if ('key' in obj && obj.key != null) return `key:${obj.key}`;
    // Fall back to stringified version for matching
    return JSON.stringify(item);
  }
  return String(item);
};

// Helper function to match array items
export const matchArrayItems = (
  oldArray: unknown[],
  newArray: unknown[],
): Array<{ oldIndex: number | null; newIndex: number | null; oldItem: unknown; newItem: unknown }> => {
  const matches: Array<{ oldIndex: number | null; newIndex: number | null; oldItem: unknown; newItem: unknown }> = [];
  const oldMatched = new Set<number>();
  const newMatched = new Set<number>();

  // First pass: try to match items by their key
  for (let i = 0; i < oldArray.length; i++) {
    if (oldMatched.has(i)) continue;
    const oldKey = getItemKey(oldArray[i]);
    for (let j = 0; j < newArray.length; j++) {
      if (newMatched.has(j)) continue;
      const newKey = getItemKey(newArray[j]);
      if (oldKey === newKey) {
        matches.push({ oldIndex: i, newIndex: j, oldItem: oldArray[i], newItem: newArray[j] });
        oldMatched.add(i);
        newMatched.add(j);
        break;
      }
    }
  }

  // Second pass: match remaining items by deep equality
  for (let i = 0; i < oldArray.length; i++) {
    if (oldMatched.has(i)) continue;
    for (let j = 0; j < newArray.length; j++) {
      if (newMatched.has(j)) continue;
      if (JSON.stringify(oldArray[i]) === JSON.stringify(newArray[j])) {
        matches.push({ oldIndex: i, newIndex: j, oldItem: oldArray[i], newItem: newArray[j] });
        oldMatched.add(i);
        newMatched.add(j);
        break;
      }
    }
  }

  // Add unmatched items
  for (let i = 0; i < oldArray.length; i++) {
    if (!oldMatched.has(i)) {
      matches.push({ oldIndex: i, newIndex: null, oldItem: oldArray[i], newItem: undefined });
    }
  }
  for (let j = 0; j < newArray.length; j++) {
    if (!newMatched.has(j)) {
      matches.push({ oldIndex: null, newIndex: j, oldItem: undefined, newItem: newArray[j] });
    }
  }

  return matches;
};

export type ChangeItem = {
  type: 'added' | 'removed' | 'changed';
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
};

// Helper function to handle array changes
const handleArrayChanges = (oldArray: unknown[], newArray: unknown[], path: string): ChangeItem[] => {
  const changes: ChangeItem[] = [];

  if (oldArray.length === 0 && newArray.length === 0) {
    // Both empty, no changes
    return changes;
  }

  // Match array items
  const matches = matchArrayItems(oldArray, newArray);

  matches.forEach((match) => {
    if (match.oldIndex == null && match.newIndex != null) {
      // Item was added
      const fieldPath = path !== '' ? `${path}[${match.newIndex}]` : `[${match.newIndex}]`;
      const parsedNewItem = parseIfJSON(match.newItem);
      if (parsedNewItem != null && typeof parsedNewItem === 'object' && !Array.isArray(parsedNewItem)) {
        changes.push(...flattenChanges(undefined, parsedNewItem, fieldPath));
      } else {
        changes.push({ type: 'added', field: fieldPath, newValue: parsedNewItem });
      }
    } else if (match.oldIndex != null && match.newIndex == null) {
      // Item was removed
      const fieldPath = path !== '' ? `${path}[${match.oldIndex}]` : `[${match.oldIndex}]`;
      const parsedOldItem = parseIfJSON(match.oldItem);
      if (parsedOldItem != null && typeof parsedOldItem === 'object' && !Array.isArray(parsedOldItem)) {
        changes.push(...flattenChanges(parsedOldItem, undefined, fieldPath));
      } else {
        changes.push({ type: 'removed', field: fieldPath, oldValue: parsedOldItem });
      }
    } else if (match.oldIndex != null && match.newIndex != null) {
      // Item exists in both - check if it changed
      const parsedOldItem = parseIfJSON(match.oldItem);
      const parsedNewItem = parseIfJSON(match.newItem);
      const nestedChanges = flattenChanges(
        parsedOldItem,
        parsedNewItem,
        path !== '' ? `${path}[${match.newIndex}]` : `[${match.newIndex}]`,
      );
      if (nestedChanges.length > 0) {
        changes.push(...nestedChanges);
      }
    }
  });

  return changes;
};

// Helper function to handle object changes
const handleObjectChanges = (
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  path: string,
): ChangeItem[] => {
  const changes: ChangeItem[] = [];
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

  // Check if this is a "campaign" object - skip the campaign level and flatten directly
  const isCampaignObject = isCampaignPath(path);
  // Check if this is an "fsm" object under campaign - skip the fsm level too
  const isFsmUnderCampaign = isFsmUnderCampaignPath(path);

  allKeys.forEach((key) => {
    let fieldPath: string;
    if (isCampaignObject) {
      // Skip "campaign" level - use key directly
      fieldPath = key;
    } else if (isFsmUnderCampaign) {
      // Skip "fsm" level under campaign - use key directly (campaign already skipped)
      fieldPath = key;
    } else {
      // Normal path building
      fieldPath = path !== '' ? `${path}.${key}` : key;
    }
    const oldVal = oldObj[key];
    const newVal = newObj[key];

    if (!(key in oldObj)) {
      // Field was added - recursively flatten if it's an object
      const parsedNewVal = parseIfJSON(newVal);
      if (parsedNewVal != null && typeof parsedNewVal === 'object' && !Array.isArray(parsedNewVal)) {
        // If we're processing "fsm" under campaign, skip the fsm level
        let nestedPath = fieldPath;
        if (isCampaignObject && key === 'fsm') {
          nestedPath = '';
        }
        changes.push(...flattenChanges(undefined, parsedNewVal, nestedPath));
      } else {
        changes.push({ type: 'added', field: fieldPath, newValue: parsedNewVal });
      }
    } else if (!(key in newObj)) {
      // Field was removed - recursively flatten if it's an object
      const parsedOldVal = parseIfJSON(oldVal);
      if (parsedOldVal != null && typeof parsedOldVal === 'object' && !Array.isArray(parsedOldVal)) {
        // If we're processing "fsm" under campaign, skip the fsm level
        let nestedPath = fieldPath;
        if (isCampaignObject && key === 'fsm') {
          nestedPath = '';
        }
        changes.push(...flattenChanges(parsedOldVal, undefined, nestedPath));
      } else {
        changes.push({ type: 'removed', field: fieldPath, oldValue: parsedOldVal });
      }
    } else {
      // Field exists in both - recursively check if it changed
      const parsedOldVal = parseIfJSON(oldVal);
      const parsedNewVal = parseIfJSON(newVal);

      // If we're processing "fsm" under campaign, skip the fsm level
      let nestedPath = fieldPath;
      if (isCampaignObject && key === 'fsm') {
        // Skip "fsm" level - use empty path so children are at root level
        nestedPath = '';
      }

      const nestedChanges = flattenChanges(parsedOldVal, parsedNewVal, nestedPath);
      if (nestedChanges.length > 0) {
        changes.push(...nestedChanges);
      }
    }
  });

  return changes;
};

// Helper function to handle primitive changes
const handlePrimitiveChanges = (oldValue: unknown, newValue: unknown, path: string): ChangeItem[] => {
  const changes: ChangeItem[] = [];
  const oldValueStr = JSON.stringify(oldValue);
  const newValueStr = JSON.stringify(newValue);
  if (oldValueStr !== newValueStr) {
    changes.push({ type: 'changed', field: path, oldValue, newValue });
  }
  return changes;
};

// Helper function to flatten nested changes into individual field changes
export const flattenChanges = (oldValue: unknown, newValue: unknown, path = ''): ChangeItem[] => {
  // Parse JSON strings before processing
  const parsedOldValue = parseIfJSON(oldValue);
  const parsedNewValue = parseIfJSON(newValue);

  // Handle arrays
  if (Array.isArray(parsedOldValue) || Array.isArray(parsedNewValue)) {
    const oldArray = Array.isArray(parsedOldValue) ? parsedOldValue : [];
    const newArray = Array.isArray(parsedNewValue) ? parsedNewValue : [];
    return handleArrayChanges(oldArray, newArray, path);
  }

  // If both are objects (and not arrays or null), recursively flatten them
  if (
    parsedOldValue != null &&
    parsedNewValue != null &&
    typeof parsedOldValue === 'object' &&
    typeof parsedNewValue === 'object' &&
    !Array.isArray(parsedOldValue) &&
    !Array.isArray(parsedNewValue)
  ) {
    const oldObj = parsedOldValue as Record<string, unknown>;
    const newObj = parsedNewValue as Record<string, unknown>;
    return handleObjectChanges(oldObj, newObj, path);
  }

  // For non-objects or arrays, do simple comparison
  return handlePrimitiveChanges(parsedOldValue, parsedNewValue, path);
};

// Helper function to compute simplified diff
export const computeSimplifiedDiff = (
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>,
): ChangeItem[] => {
  const changes: ChangeItem[] = [];
  const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

  allKeys.forEach((key) => {
    const oldValue = parseIfJSON(oldData[key]);
    const newValue = parseIfJSON(newData[key]);

    if (!(key in oldData)) {
      // Field was added - flatten nested objects
      changes.push(...flattenChanges(undefined, newValue, key));
    } else if (!(key in newData)) {
      // Field was removed - flatten nested objects
      changes.push(...flattenChanges(oldValue, undefined, key));
    } else {
      // Field exists in both - flatten nested changes
      changes.push(...flattenChanges(oldValue, newValue, key));
    }
  });

  return changes;
};

// Helper function to recursively extract only changed fields from nested objects
export const extractChangedFieldsRecursive = (
  oldValue: unknown,
  newValue: unknown,
): { oldChanged: unknown; newChanged: unknown } | null => {
  // If both are objects (and not arrays or null), recursively diff them
  if (
    oldValue != null &&
    newValue != null &&
    typeof oldValue === 'object' &&
    typeof newValue === 'object' &&
    !Array.isArray(oldValue) &&
    !Array.isArray(newValue)
  ) {
    const oldObj = oldValue as Record<string, unknown>;
    const newObj = newValue as Record<string, unknown>;
    const oldChanged: Record<string, unknown> = {};
    const newChanged: Record<string, unknown> = {};
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    let hasChanges = false;

    allKeys.forEach((key) => {
      const oldVal = oldObj[key];
      const newVal = newObj[key];

      if (!(key in oldObj)) {
        // Field was added
        newChanged[key] = newVal;
        hasChanges = true;
      } else if (!(key in newObj)) {
        // Field was removed
        oldChanged[key] = oldVal;
        hasChanges = true;
      } else {
        // Field exists in both - recursively check if it changed
        const nestedChanges = extractChangedFieldsRecursive(oldVal, newVal);
        if (nestedChanges != null) {
          oldChanged[key] = nestedChanges.oldChanged;
          newChanged[key] = nestedChanges.newChanged;
          hasChanges = true;
        }
      }
    });

    return hasChanges ? { oldChanged, newChanged } : null;
  }

  // For non-objects or arrays, do simple comparison
  const oldValueStr = JSON.stringify(oldValue);
  const newValueStr = JSON.stringify(newValue);
  if (oldValueStr !== newValueStr) {
    return { oldChanged: oldValue, newChanged: newValue };
  }

  return null;
};

// Helper function to extract only changed fields for diff
export const extractChangedFields = (
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>,
): { oldChanged: Record<string, unknown>; newChanged: Record<string, unknown> } => {
  const oldChanged: Record<string, unknown> = {};
  const newChanged: Record<string, unknown> = {};
  const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

  allKeys.forEach((key) => {
    const oldValue = oldData[key];
    const newValue = newData[key];

    if (!(key in oldData)) {
      // Field was added
      newChanged[key] = newValue;
    } else if (!(key in newData)) {
      // Field was removed
      oldChanged[key] = oldValue;
    } else {
      // Field exists in both - recursively check if it changed
      const changes = extractChangedFieldsRecursive(oldValue, newValue);
      if (changes != null) {
        oldChanged[key] = changes.oldChanged;
        newChanged[key] = changes.newChanged;
      }
    }
  });

  return { oldChanged, newChanged };
};

// Helper function to extract changed field names (for the Changes column)
export const getChangedFieldNames = (oldValue: unknown, newValue: unknown, path = ''): string[] => {
  const fields: string[] = [];

  // Parse JSON strings before processing
  const parsedOldValue = parseIfJSON(oldValue);
  const parsedNewValue = parseIfJSON(newValue);

  // If both are objects (and not arrays or null), recursively get field names
  if (
    parsedOldValue != null &&
    parsedNewValue != null &&
    typeof parsedOldValue === 'object' &&
    typeof parsedNewValue === 'object' &&
    !Array.isArray(parsedOldValue) &&
    !Array.isArray(parsedNewValue)
  ) {
    const oldObj = parsedOldValue as Record<string, unknown>;
    const newObj = parsedNewValue as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

    // Check if this is a "campaign" object - skip the campaign level and flatten directly
    const isCampaignObject = isCampaignPath(path);

    allKeys.forEach((key) => {
      let fieldPath: string;
      if (isCampaignObject) {
        // Skip "campaign" level - use key directly
        fieldPath = key;
      } else {
        // Normal path building
        fieldPath = path !== '' ? `${path}.${key}` : key;
      }

      const oldVal = oldObj[key];
      const newVal = newObj[key];

      if (!(key in oldObj) || !(key in newObj)) {
        // Field was added or removed - recursively get field names if it's an object
        const val = key in oldObj ? oldVal : newVal;
        const parsedVal = parseIfJSON(val);
        if (parsedVal != null && typeof parsedVal === 'object' && !Array.isArray(parsedVal)) {
          // If we're processing "fsm" under campaign, skip the fsm level
          let nestedPath = fieldPath;
          if (isCampaignObject && key === 'fsm') {
            nestedPath = '';
          }
          fields.push(
            ...getChangedFieldNames(
              key in oldObj ? parsedVal : undefined,
              key in newObj ? parsedVal : undefined,
              nestedPath,
            ),
          );
        } else {
          fields.push(fieldPath);
        }
      } else {
        // Field exists in both - recursively check if it changed
        const parsedOldVal = parseIfJSON(oldVal);
        const parsedNewVal = parseIfJSON(newVal);

        // If we're processing "fsm" under campaign, skip the fsm level
        let nestedPath = fieldPath;
        if (isCampaignObject && key === 'fsm') {
          nestedPath = '';
        }

        const nestedFields = getChangedFieldNames(parsedOldVal, parsedNewVal, nestedPath);
        if (nestedFields.length > 0) {
          fields.push(...nestedFields);
        } else {
          // Check if the values actually changed
          const oldValueStr = JSON.stringify(parsedOldVal);
          const newValueStr = JSON.stringify(parsedNewVal);
          if (oldValueStr !== newValueStr) {
            fields.push(fieldPath);
          }
        }
      }
    });
  } else {
    // For non-objects or arrays, do simple comparison
    const oldValueStr = JSON.stringify(parsedOldValue);
    const newValueStr = JSON.stringify(parsedNewValue);
    if (oldValueStr !== newValueStr) {
      fields.push(path);
    }
  }

  return fields;
};
